<template>
  <section class="auto-reply">
    <h2>{{ $t('account_setting.auto_reply') }}</h2>
    <el-switch v-model="form.enabled" class="auto-reply__switch" @change="emitChange" />
    <p class="auto-reply__tip">{{ $t('account_setting.auto_reply.tip') }}</p>
    <el-input
        v-model="form.content"
        class="auto-reply__content"
        type="textarea"
        resize="none"
        :rows="5"
        :disabled="!form.enabled"
        :placeholder="$t('account_setting.auto_reply.placeholder')"
        @change="emitChange"
    />

    <div class="auto-reply__field auto-reply__time-field">
      <label>{{ $t('account_setting.auto_reply.time') }}</label>
      <el-select
          v-model="form.timeMode"
          size="small"
          :disabled="!form.enabled"
          class="auto-reply__mode-select"
          @change="emitChange"
      >
        <el-option :label="$t('account_setting.auto_reply.always')" value="always" />
        <el-option :label="$t('account_setting.auto_reply.scheduled')" value="scheduled" />
      </el-select>
      <div v-if="form.timeMode === 'scheduled'" class="auto-reply__schedule">
        <div class="auto-reply__schedule-pair">
          <el-date-picker
              v-model="form.startDate"
              type="date"
              size="small"
              class="auto-reply__schedule-date"
              :disabled="!form.enabled"
              :placeholder="$t('account_setting.auto_reply.start')"
              value-format="YYYY-MM-DD"
              @change="handleStartDateChange"
          />
          <el-time-picker
              v-model="form.startTime"
              size="small"
              class="auto-reply__schedule-time"
              :disabled="!form.enabled"
              value-format="HH:mm:ss"
              @change="handleStartTimeChange"
          />
        </div>
        <span class="auto-reply__schedule-to">{{ $t('account_setting.auto_reply.to') }}</span>
        <div class="auto-reply__schedule-pair">
          <el-date-picker
              v-model="form.endDate"
              type="date"
              size="small"
              class="auto-reply__schedule-date"
              :disabled="!form.enabled"
              :disabled-date="disabledEndDate"
              :placeholder="$t('account_setting.auto_reply.end')"
              value-format="YYYY-MM-DD"
              @change="handleEndDateChange"
          />
          <el-time-picker
              v-model="form.endTime"
              size="small"
              class="auto-reply__schedule-time"
              :disabled="!form.enabled"
              :disabled-hours="disabledEndHours"
              :disabled-minutes="disabledEndMinutes"
              :disabled-seconds="disabledEndSeconds"
              value-format="HH:mm:ss"
              @change="handleEndTimeChange"
          />
        </div>
      </div>
    </div>

    <div class="auto-reply__field auto-reply__scope">
      <label>{{ $t('account_setting.auto_reply.scope') }}</label>
      <el-checkbox v-model="form.onlyArchivedCustomers" :disabled="!form.enabled" @change="emitChange">
        {{ $t('account_setting.auto_reply.only_archived') }}
      </el-checkbox>
    </div>

    <div class="auto-reply__field auto-reply__mailbox-field">
      <label>{{ $t('account_setting.auto_reply.mailbox') }}</label>
      <el-select
          v-model="form.mailboxes"
          size="small"
          multiple
          collapse-tags
          collapse-tags-tooltip
          :disabled="!form.enabled || !mailboxOptions.length"
          :placeholder="$t('account_setting.auto_reply.select_mailbox')"
          class="auto-reply__mailbox-select"
          @change="emitChange"
      >
        <el-option
            v-for="item in mailboxOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
        />
      </el-select>
      <p v-if="!mailboxOptions.length" class="auto-reply__mailbox-tip">
        {{ $t('account_setting.auto_reply.no_mailbox') }}
      </p>
    </div>

    <div class="auto-reply__actions">
      <el-button type="primary" size="small" :loading="saving" @click="$emit('save')">
        {{ $t('common.save') }}
      </el-button>
    </div>
  </section>
</template>

<script>
import {ElMessage} from 'element-plus'

export default {
  name: 'MailAutoReply',
  props: {
    modelValue: {
      type: Object,
      default: () => ({})
    },
    mailboxOptions: {
      type: Array,
      default: () => []
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'save'],
  data() {
    return {
      hasInitializedForm: false,
      form: {
        enabled: false,
        content: this.$t('account_setting.auto_reply.default_content'),
        timeMode: 'always',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        onlyArchivedCustomers: false,
        mailboxes: []
      }
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      deep: true,
      handler(value) {
        const incomingContent = value?.content
        this.form = {
          ...this.form,
          ...(value || {}),
          content: !this.hasInitializedForm && !String(incomingContent || '').trim()
            ? this.$t('account_setting.auto_reply.default_content')
            : incomingContent ?? this.form.content,
          mailboxes: Array.isArray(value?.mailboxes) ? value.mailboxes : []
        }
        this.hasInitializedForm = true
        this.normalizeScheduleRange()
      }
    },
    mailboxOptions: {
      deep: true,
      handler(options) {
        if (!options.length) {
          return
        }
        const allowed = new Set(options.map(item => item.value))
        const mailboxByAddress = new Map(
            options.map(item => [String(item.label || '').toLowerCase(), item.value])
        )
        const validMailboxes = this.form.mailboxes
            .map(value => allowed.has(value)
                ? value
                : mailboxByAddress.get(String(value || '').toLowerCase()))
            .filter(value => value !== undefined)
        if (JSON.stringify(validMailboxes) !== JSON.stringify(this.form.mailboxes)) {
          this.form.mailboxes = validMailboxes
          this.emitChange()
        }
      }
    }
  },
  methods: {
    getDateParts(value) {
      if (value instanceof Date) {
        return [value.getFullYear(), value.getMonth() + 1, value.getDate()]
      }
      const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
      return match ? match.slice(1).map(Number) : null
    },
    getDateValue(value) {
      const parts = this.getDateParts(value)
      if (!parts) {
        return null
      }
      const [year, month, day] = parts
      const timestamp = Date.UTC(year, month - 1, day)
      return Number.isNaN(timestamp) ? null : timestamp
    },
    getTimeParts(value) {
      const match = String(value || '').match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/)
      if (!match) {
        return null
      }
      const parts = match.slice(1).map(Number)
      const [hour, minute, second = 0] = parts
      if (hour > 23 || minute > 59 || second > 59) {
        return null
      }
      return [hour, minute, second]
    },
    getTimeValue(value) {
      const parts = this.getTimeParts(value)
      return parts ? parts[0] * 3600 + parts[1] * 60 + parts[2] : null
    },
    isSameScheduleDate() {
      const startDate = this.getDateValue(this.form.startDate)
      const endDate = this.getDateValue(this.form.endDate)
      return startDate !== null && startDate === endDate
    },
    normalizeScheduleRange() {
      const startDate = this.getDateValue(this.form.startDate)
      const endDate = this.getDateValue(this.form.endDate)
      if (startDate === null || endDate === null) {
        return false
      }

      if (endDate < startDate) {
        this.form.endDate = ''
        this.form.endTime = ''
        return true
      }

      if (endDate === startDate) {
        const startTime = this.getTimeValue(this.form.startTime)
        const endTime = this.getTimeValue(this.form.endTime)
        if (startTime !== null && endTime !== null && endTime <= startTime) {
          this.form.endTime = ''
          return true
        }
      }
      return false
    },
    handleScheduleChange() {
      const changed = this.normalizeScheduleRange()
      if (changed) {
        ElMessage.warning(this.$t('account_setting.auto_reply.invalid_time_range'))
      }
      this.emitChange()
    },
    handleStartDateChange() {
      this.handleScheduleChange()
    },
    handleStartTimeChange() {
      this.handleScheduleChange()
    },
    handleEndDateChange() {
      this.handleScheduleChange()
    },
    handleEndTimeChange() {
      this.handleScheduleChange()
    },
    disabledEndDate(date) {
      const startDate = this.getDateValue(this.form.startDate)
      return startDate !== null && this.getDateValue(date) < startDate
    },
    disabledEndHours() {
      if (!this.isSameScheduleDate()) {
        return []
      }
      const startTime = this.getTimeParts(this.form.startTime)
      return startTime ? Array.from({length: startTime[0] + 1}, (_, hour) => hour) : []
    },
    disabledEndMinutes(hour) {
      if (!this.isSameScheduleDate()) {
        return []
      }
      const startTime = this.getTimeParts(this.form.startTime)
      return startTime && hour === startTime[0]
        ? Array.from({length: startTime[1] + 1}, (_, minute) => minute)
        : []
    },
    disabledEndSeconds(hour, minute) {
      if (!this.isSameScheduleDate()) {
        return []
      }
      const startTime = this.getTimeParts(this.form.startTime)
      return startTime && hour === startTime[0] && minute === startTime[1]
        ? Array.from({length: startTime[2] + 1}, (_, second) => second)
        : []
    },
    emitChange() {
      this.$emit('update:modelValue', JSON.parse(JSON.stringify(this.form)))
    }
  }
}
</script>

<style lang="less" scoped>
.auto-reply {
  min-height: 520px;
  padding: 10px;
  background: #fff;
  font-size: 13px;
  --el-font-size-base: 13px;
  --el-font-size-small: 13px;
  --el-font-size-extra-small: 13px;

  h2 {
    margin: 0 0 13px;
    color: #303133;
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
  }
}

.auto-reply__switch {
  display: block;
  margin: 0 0 8px;
}

.auto-reply__tip,
.auto-reply__mailbox-tip {
  margin: 0;
  color: #909399;
  font-size: 13px;
  line-height: 18px;
}

.auto-reply__content {
  display: block;
  width: 772px;
  max-width: 100%;
  margin-top: 8px;

  :deep(.el-textarea__inner) {
    box-sizing: border-box;
    height: 109px !important;
    min-height: 109px !important;
    padding: 9px 10px;
    font-size: 13px;
    line-height: 20px;
  }
}

.auto-reply__field {
  width: 772px;
  max-width: 100%;
  margin-top: 18px;

  > label {
    display: block;
    margin-bottom: 7px;
    color: #606266;
    font-size: 13px;
    line-height: 18px;
  }
}

.auto-reply .auto-reply__mode-select,
.auto-reply .auto-reply__mailbox-select {
  width: 300px !important;
  height: 30px !important;
  min-height: 30px !important;
  margin: 0 !important;

  :deep(.el-select__wrapper) {
    box-sizing: border-box;
    height: 30px;
    min-height: 30px;
    align-items: center;
  }
}

.auto-reply :deep(.el-select__wrapper),
.auto-reply :deep(.el-select__selected-item),
.auto-reply :deep(.el-select__placeholder),
.auto-reply :deep(.el-input__inner),
.auto-reply :deep(.el-checkbox__label) {
  font-size: 13px;
}

.auto-reply :deep(.el-tag) {
  --el-tag-font-size: 13px;
  font-size: 13px;
}

.auto-reply__schedule {
  display: flex;
  align-items: center;
  width: 776px;
  max-width: 100%;
  margin-top: 7px;
  gap: 4px;
  white-space: nowrap;
}

.auto-reply__schedule-pair {
  display: grid;
  grid-template-columns: 186px 186px;
  gap: 4px;
  flex: 0 0 auto;

  :deep(.auto-reply__schedule-date),
  :deep(.auto-reply__schedule-time) {
    width: 186px !important;
    height: 30px !important;
    min-height: 30px !important;
    min-width: 0;
    margin: 0 !important;
  }
}

.auto-reply__schedule-date :deep(.el-input__wrapper),
.auto-reply__schedule-time :deep(.el-input__wrapper) {
  box-sizing: border-box;
  height: 30px;
  min-height: 30px;
  align-items: center;
}

.auto-reply__schedule-time :deep(.el-input__wrapper) {
  padding-right: 14px;
}

.auto-reply__schedule-to {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  color: #606266;
  font-size: 13px;
  line-height: 26px;
}

.auto-reply__scope {
  margin-top: 18px;

  :deep(.el-checkbox) {
    display: inline-flex;
    align-items: center;
    height: 30px;
    margin: 0;
    line-height: 30px;
  }

  :deep(.el-checkbox__input) {
    display: inline-flex;
    align-items: center;
    height: 30px;
  }

  :deep(.el-checkbox__label) {
    padding-left: 8px;
    color: #606266;
    font-size: 13px;
    font-weight: 400 !important;
    line-height: 30px;
  }
}

.auto-reply__mailbox-field {
  margin-top: 25px;
}

.auto-reply__mailbox-tip {
  margin-top: 6px;
}

.auto-reply__actions {
  margin-top: 24px;
}

@media (max-width: 860px) {
  .auto-reply__schedule {
    align-items: flex-start;
    flex-direction: column;
    gap: 7px;
    white-space: normal;
  }

  .auto-reply__schedule-pair {
    width: 100%;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);

    :deep(.auto-reply__schedule-date),
    :deep(.auto-reply__schedule-time) {
      width: 100% !important;
      min-width: 0;
    }
  }

  .auto-reply__schedule-to {
    width: auto;
    height: 18px;
    line-height: 18px;
  }
}
</style>
